/**
 * Per-program exception to the 13-month placeholder discovery window.
 *
 * A variation in `campLocations.variations[]` may carry `placeholderDiscoveryUntil: "YYYY-MM-DD"`.
 * While today's UTC date is on or before that day, the program keeps (or gets) its year-3008
 * placeholder even with no qualifying real session. After it, the normal 13-month rule decides.
 *
 * Pure and dependency-free on purpose: the admin-SDK port parity-tests against this module.
 */

const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Summer is Jun–Aug; the exception ends on Sept 1 of the first summer that starts after the set day. */
const SUMMER_START_MONTH = 5; // June, 0-based
const EXPIRY_MONTH = 8; // September, 0-based
const EXPIRY_DAY = 1;

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

export function utcDayString(d: Date): string {
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

/**
 * The `placeholderDiscoveryUntil` value for a flag set at `setAt` (UTC calendar day).
 *
 * Sept 1 of the first summer (Jun 1 – Aug 31) that starts strictly after the set day:
 * - set before Jun 1 of year Y → Y-09-01
 * - set on or after Jun 1 of year Y (mid-summer included) → (Y+1)-09-01
 */
export function computePlaceholderDiscoveryUntil(setAt: Date): string {
  if (!(setAt instanceof Date) || Number.isNaN(setAt.getTime())) {
    throw new Error("computePlaceholderDiscoveryUntil: setAt must be a valid Date");
  }
  const y = setAt.getUTCFullYear();
  const beforeSummerStarts = setAt.getUTCMonth() < SUMMER_START_MONTH;
  const expiryYear = beforeSummerStarts ? y : y + 1;
  return `${expiryYear}-${pad2(EXPIRY_MONTH + 1)}-${pad2(EXPIRY_DAY)}`;
}

export type PlaceholderDiscoveryUntilParse =
  | { kind: "absent" }
  | { kind: "valid"; day: string }
  | { kind: "invalid"; raw: unknown };

/**
 * Classify a stored `placeholderDiscoveryUntil`. `null`/`undefined`/`""` are absent; anything
 * else that is not a real calendar day in `YYYY-MM-DD` form is invalid (and grants nothing).
 */
export function parsePlaceholderDiscoveryUntil(value: unknown): PlaceholderDiscoveryUntilParse {
  if (value == null) return { kind: "absent" };
  if (typeof value !== "string") return { kind: "invalid", raw: value };
  const s = value.trim();
  if (s === "") return { kind: "absent" };
  const m = DAY_RE.exec(s);
  if (!m) return { kind: "invalid", raw: value };
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const probe = new Date(Date.UTC(y, mo - 1, d));
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== mo - 1 || probe.getUTCDate() !== d) {
    return { kind: "invalid", raw: value };
  }
  return { kind: "valid", day: s };
}

/** Inclusive: the exception still holds on the until day itself (UTC). */
function isTodayOnOrBefore(day: string, now: Date): boolean {
  return utcDayString(now) <= day;
}

/**
 * The writer (a server, another admin's browser) and the evaluator do not share a clock. Without
 * slack, a flag written just after Jun 1 UTC by a clock running ahead would be rejected — and its
 * placeholder deleted — by an evaluator still on May 31.
 */
const REACHABLE_EXPIRY_SLACK_DAYS = 7;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * A day later than `computePlaceholderDiscoveryUntil(now + slack)` cannot come from the rule: the rule
 * never decreases as the set day moves later, so any flag set on or before today (by a clock at most
 * the slack ahead) expires on or before that bound. Such a value is treated as invalid rather than as
 * a permanent exception.
 */
function isBeyondReachableExpiry(day: string, now: Date): boolean {
  const latestWriterClock = new Date(now.getTime() + REACHABLE_EXPIRY_SLACK_DAYS * MS_PER_DAY);
  return day > computePlaceholderDiscoveryUntil(latestWriterClock);
}

export type PlaceholderDiscoveryUntilProblem = "malformed" | "beyond-reachable-expiry";

/** True when `until` is a valid, reachable day and today's UTC date is on or before it. */
export function isPlaceholderDiscoveryExceptionActive(until: unknown, now: Date = new Date()): boolean {
  const parsed = parsePlaceholderDiscoveryUntil(until);
  return parsed.kind === "valid" && !isBeyondReachableExpiry(parsed.day, now) && isTodayOnOrBefore(parsed.day, now);
}

export interface PlaceholderDiscoveryVariation {
  key?: unknown;
  placeholderDiscoveryUntil?: unknown;
}

export interface PlaceholderDiscoveryExceptionKeys {
  /** Variation keys whose exception is active today. */
  activeKeys: Set<string>;
  /**
   * Variations whose value grants nothing: not a real `YYYY-MM-DD` day, or later than any flag the
   * expiry rule could have produced by today.
   */
  invalid: Array<{ key: string; raw: unknown; problem: PlaceholderDiscoveryUntilProblem }>;
}

export function collectPlaceholderDiscoveryExceptionKeys(
  variations: readonly PlaceholderDiscoveryVariation[] | null | undefined,
  now: Date = new Date()
): PlaceholderDiscoveryExceptionKeys {
  const activeKeys = new Set<string>();
  const invalid: PlaceholderDiscoveryExceptionKeys["invalid"] = [];
  if (!Array.isArray(variations)) return { activeKeys, invalid };
  for (const v of variations) {
    if (!v || typeof v !== "object") continue;
    const key = typeof v.key === "string" ? v.key.trim() : "";
    if (!key) continue;
    const parsed = parsePlaceholderDiscoveryUntil(v.placeholderDiscoveryUntil);
    if (parsed.kind === "invalid") {
      invalid.push({ key, raw: parsed.raw, problem: "malformed" });
      continue;
    }
    if (parsed.kind !== "valid") continue;
    if (isBeyondReachableExpiry(parsed.day, now)) {
      invalid.push({ key, raw: v.placeholderDiscoveryUntil, problem: "beyond-reachable-expiry" });
      continue;
    }
    if (isTodayOnOrBefore(parsed.day, now)) activeKeys.add(key);
  }
  return { activeKeys, invalid };
}
